import { db, auth } from './firebase';
import { doc, getDoc, setDoc, serverTimestamp, updateDoc, collection, query, where, onSnapshot, getDocs } from 'firebase/firestore';

// Costanti per gli stati dell'abbonamento
const SUBSCRIPTION_STATES = {
  ACTIVE: 'active',
  EXPIRED: 'expired',
  CANCELED: 'canceled',
  PAST_DUE: 'past_due',
  UNPAID: 'unpaid'
};

// Cache per gli abbonamenti Stripe
let stripeSubscriptionsCache = {
  data: null,
  timestamp: 0
};

// Tempo di validità della cache (2 minuti)
const CACHE_VALIDITY_TIME = 2 * 60 * 1000;

const subscriptionService = {
  /**
   * Ottiene l'abbonamento corrente con un aggiornamento opzionale in un'unica operazione
   * per ridurre le chiamate duplicate a Firestore
   */
  async getWithOptionalRefresh() {
    try {
      if (!auth.currentUser) {
        throw new Error('Utente non autenticato');
      }

      const userId = auth.currentUser.uid;

      // Riferimenti ai documenti
      const subscriptionRef = doc(db, `users/${userId}/subscription/default`);
      
      // Ottieni l'abbonamento corrente se esiste
      const subscriptionSnap = await getDoc(subscriptionRef);
      const currentSubscription = subscriptionSnap.exists() ? subscriptionSnap.data() : null;
      
      // Verifica se è necessario aggiornare l'abbonamento
      const now = Date.now();
      
      // Se l'abbonamento è premium e ha un updatedAt recente, non è necessario aggiornarlo
      if (currentSubscription && 
          currentSubscription.updatedAt && 
          currentSubscription.plan === 'premium' &&
          (now - currentSubscription.updatedAt.toMillis()) < CACHE_VALIDITY_TIME) {
        return { data: currentSubscription, refreshed: false };
      }
      
      // Se l'abbonamento è gratuito e ha un updatedAt, non è necessario aggiornarlo
      if (currentSubscription && 
          currentSubscription.updatedAt && 
          currentSubscription.plan === 'free') {
        return { data: currentSubscription, refreshed: false };
      }
      
      // Se arriviamo qui, è necessario aggiornare l'abbonamento
      // Recupera gli abbonamenti Stripe dalla cache o da Firestore
      const stripeSubscriptions = await this.getStripeSubscriptionsWithCache(userId);
      
      if (stripeSubscriptions && stripeSubscriptions.length > 0) {
        // Ordina gli abbonamenti per data di creazione (più recente prima)
        const sortedSubscriptions = this.sortSubscriptionsByRecency(stripeSubscriptions);
        
        // Usa il primo abbonamento dopo l'ordinamento (il più recente)
        const stripeSubscription = sortedSubscriptions[0];
        
        // Determina se l'abbonamento è attivo in base allo stato
        const isActive = stripeSubscription.status === 'active';
        
        // Crea l'oggetto abbonamento aggiornato
        const updatedSubscription = {
          plan: 'premium',
          status: stripeSubscription.status || 'active',
          isActive: isActive,
          deviceLimit: Infinity,
          partLimit: Infinity,
          startDate: stripeSubscription.current_period_start || serverTimestamp(),
          endDate: stripeSubscription.current_period_end || null,
          cancelAtPeriodEnd: stripeSubscription.cancel_at_period_end || false,
          canceledAt: stripeSubscription.canceled_at || null,
          stripeCustomerId: stripeSubscription.customer || null,
          stripeSubscriptionId: stripeSubscription.id || null,
          requiresPaymentMethod: stripeSubscription.status === 'past_due' || stripeSubscription.status === 'unpaid',
          updatedAt: serverTimestamp(),
          // Mantieni i contatori esistenti o inizializzali a 0
          devicesCreated: currentSubscription?.devicesCreated || 0,
          partsCreated: currentSubscription?.partsCreated || 0
        };
        
        await setDoc(subscriptionRef, updatedSubscription, { merge: true });
        return { data: updatedSubscription, refreshed: true };
      }
      
      // Se non ci sono abbonamenti Stripe ma esiste un abbonamento corrente, restituiscilo
      if (currentSubscription) {
        // Aggiorna il timestamp per evitare verifiche frequenti
        if (currentSubscription.updatedAt === undefined) {
          await updateDoc(subscriptionRef, { updatedAt: serverTimestamp() });
          currentSubscription.updatedAt = serverTimestamp();
        }
        return { data: currentSubscription, refreshed: false };
      }
      
      // Crea un nuovo abbonamento gratuito se non esiste
      const freeSubscription = {
        plan: 'free',
        status: 'active',
        isActive: true,
        deviceLimit: 20,
        partLimit: 20,
        startDate: serverTimestamp(),
        endDate: null,
        devicesCreated: 0,
        partsCreated: 0,
        updatedAt: serverTimestamp()
      };
      
      await setDoc(subscriptionRef, freeSubscription);
      return { data: freeSubscription, refreshed: true };
    } catch (error) {
      console.error('Errore nel recupero e aggiornamento dell\'abbonamento:', error);
      throw error;
    }
  },
  
  /**
   * Recupera gli abbonamenti Stripe con caching per ridurre le chiamate a Firestore
   */
  async getStripeSubscriptionsWithCache(userId) {
    const now = Date.now();
    
    // Usa la cache se è valida
    if (stripeSubscriptionsCache.data && 
        stripeSubscriptionsCache.data.length > 0 &&
        (now - stripeSubscriptionsCache.timestamp) < CACHE_VALIDITY_TIME) {
      return stripeSubscriptionsCache.data;
    }
    
    // Altrimenti recupera da Firestore
    const stripeSubscriptionsRef = collection(db, `users/${userId}/subscriptions`);
    
    // Cerca abbonamenti Stripe attivi
    const stripeSubscriptionsSnap = await getDocs(
      query(stripeSubscriptionsRef, where('status', 'in', ['active', 'past_due', 'unpaid', 'canceled']))
    );
    
    if (!stripeSubscriptionsSnap.empty) {
      // Ottieni tutti gli abbonamenti
      const subscriptions = stripeSubscriptionsSnap.docs.map(doc => doc.data());
      
      // Aggiorna la cache
      stripeSubscriptionsCache = {
        data: subscriptions,
        timestamp: now
      };
      
      return subscriptions;
    }
    
    // Se non ci sono abbonamenti, imposta una cache vuota ma valida
    stripeSubscriptionsCache = {
      data: [],
      timestamp: now
    };
    
    return [];
  },
  
  /**
   * Ordina gli abbonamenti per data di creazione (più recente prima)
   */
  sortSubscriptionsByRecency(subscriptions) {
    return [...subscriptions].sort((a, b) => {
      // Ordina per data di creazione (se disponibile)
      if (a.created && b.created) {
        return b.created - a.created; // Il più recente prima
      }
      
      // Se non c'è data di creazione, usa la data di inizio periodo
      if (a.current_period_start && b.current_period_start) {
        return b.current_period_start - a.current_period_start;
      }
      
      return 0;
    });
  },

  async get() {
    try {
      if (!auth.currentUser) {
        throw new Error('Utente non autenticato');
      }

      const userId = auth.currentUser.uid;
      console.log('Recupero abbonamento per utente:', userId);

      // Riferimenti ai documenti
      const subscriptionRef = doc(db, `users/${userId}/subscription/default`);
      const stripeSubscriptionsRef = collection(db, `users/${userId}/subscriptions`);
      
      // Ottieni l'abbonamento corrente se esiste
      const subscriptionSnap = await getDoc(subscriptionRef);
      const currentSubscription = subscriptionSnap.exists() ? subscriptionSnap.data() : null;

      // Cerca abbonamenti Stripe attivi
      const stripeSubscriptionsSnap = await getDocs(
        query(stripeSubscriptionsRef, where('status', 'in', ['active', 'past_due', 'unpaid', 'canceled']))
      );

      // Se esiste un abbonamento Stripe
      if (!stripeSubscriptionsSnap.empty) {
        // Ottieni tutti gli abbonamenti
        const subscriptions = stripeSubscriptionsSnap.docs.map(doc => doc.data());
        console.log('Abbonamenti Stripe trovati:', subscriptions.length);
        
        // Ordina gli abbonamenti solo in base alla data di creazione, prendendo il più recente
        const sortedSubscriptions = [...subscriptions].sort((a, b) => {
          // Ordina per data di creazione (se disponibile)
          if (a.created && b.created) {
            return b.created - a.created; // Il più recente prima
          }
          
          // Se non c'è data di creazione, usa la data di inizio periodo
          if (a.current_period_start && b.current_period_start) {
            return b.current_period_start - a.current_period_start;
          }
          
          return 0;
        });
        
        // Usa il primo abbonamento dopo l'ordinamento (il più recente)
        const stripeSubscription = sortedSubscriptions[0];
        
        console.log('Abbonamento Stripe selezionato (più recente):', stripeSubscription);
        
        // Determina se l'abbonamento è attivo in base allo stato
        const isActive = stripeSubscription.status === 'active';
        
        // Crea l'oggetto abbonamento con valori predefiniti per campi potenzialmente undefined
        const subscription = {
          plan: 'premium',
          status: stripeSubscription.status || 'active',
          isActive: isActive,
          deviceLimit: Infinity,
          partLimit: Infinity,
          startDate: stripeSubscription.current_period_start || serverTimestamp(),
          endDate: stripeSubscription.current_period_end || null,
          cancelAtPeriodEnd: stripeSubscription.cancel_at_period_end || false,
          canceledAt: stripeSubscription.canceled_at || null,
          stripeCustomerId: stripeSubscription.customer || null,
          stripeSubscriptionId: stripeSubscription.id || null,
          // Aggiungi il flag requiresPaymentMethod per stati che richiedono aggiornamento del pagamento
          requiresPaymentMethod: stripeSubscription.status === 'past_due' || stripeSubscription.status === 'unpaid',
          // Mantieni i contatori esistenti o inizializzali a 0
          devicesCreated: currentSubscription?.devicesCreated || 0,
          partsCreated: currentSubscription?.partsCreated || 0
        };

        console.log('Salvataggio abbonamento aggiornato:', subscription);
        await setDoc(subscriptionRef, subscription, { merge: true });
        return { data: subscription };
      }

      // Se non c'è un abbonamento Stripe, usa l'abbonamento corrente o crea uno gratuito
      if (currentSubscription) {
        return { data: currentSubscription };
      }

      // Crea un nuovo abbonamento gratuito
      const freeSubscription = {
          plan: 'free',
          status: 'active',
        isActive: true,
          deviceLimit: 20,
        partLimit: 20,
        startDate: serverTimestamp(),
          devicesCreated: 0,
        partsCreated: 0,
          stripeCustomerId: null,
        stripeSubscriptionId: null
      };

      await setDoc(subscriptionRef, freeSubscription);
      return { data: freeSubscription };
    } catch (error) {
      console.error('Errore nel recupero dell\'abbonamento:', error);
      throw error;
    }
  },

  /**
   * Verifica centralizzata dello stato dell'abbonamento
   * @param {Object} subscription - L'oggetto abbonamento da verificare
   * @returns {Object} Stato dell'abbonamento con informazioni aggiuntive
   */
  getSubscriptionStatus(subscription) {
    if (!subscription) {
      return { 
        isActive: false, 
        state: SUBSCRIPTION_STATES.EXPIRED,
        reason: 'subscription.errors.notFound',
        canCreateContent: false
      };
    }

    // Per abbonamenti premium, leggi lo stato direttamente dal documento aggiornato da Stripe
    if (subscription.plan === 'premium' && subscription.stripeSubscriptionId) {
      // Lo stato dell'abbonamento è già aggiornato dai webhook
      const isActive = subscription.status === 'active';
      
      if (!isActive) {
        // Determina il motivo dell'inattività in base allo stato
        const reason = subscription.status === 'past_due' ? 
          'subscription.errors.paymentMethodRequired' :
          subscription.status === 'canceled' ? 
          'subscription.errors.canceled' : 
          'subscription.errors.expired';
          
        return {
          isActive: false,
          state: subscription.status,
          reason: reason,
          canCreateContent: false,
          requiresPaymentMethod: subscription.status === 'past_due'
        };
      }
      
      // Verifica se l'abbonamento è stato annullato ma è ancora attivo
      const isCanceled = subscription.cancelAtPeriodEnd === true || subscription.cancel_at_period_end === true;
      
      return {
        isActive: true,
        state: SUBSCRIPTION_STATES.ACTIVE,
        reason: null,
        canCreateContent: true,
        requiresPaymentMethod: false,
        deviceLimitReached: false,
        partLimitReached: false,
        remainingDevices: Infinity,
        remainingParts: Infinity,
        isCanceled: isCanceled
      };
    }

    // Per abbonamenti gratuiti, mantieni la logica esistente
    if (subscription.plan === 'free') {
      const deviceLimitReached = subscription.devicesCreated >= subscription.deviceLimit;
      const partLimitReached = (subscription.partsCreated || 0) >= (subscription.partLimit || 20);
      
      return {
        isActive: true,
        state: SUBSCRIPTION_STATES.ACTIVE,
        reason: null,
        canCreateContent: true,
        deviceLimitReached,
        partLimitReached,
        remainingDevices: Math.max(0, subscription.deviceLimit - subscription.devicesCreated),
        remainingParts: Math.max(0, (subscription.partLimit || 20) - (subscription.partsCreated || 0))
      };
    }

    // Verifica se l'abbonamento è scaduto
    const now = new Date();
    let endDate = null;
    
    if (subscription.endDate) {
      endDate = subscription.endDate.toDate ? 
        subscription.endDate.toDate() : 
        new Date(subscription.endDate);
    }

    // Verifica se l'abbonamento richiede un aggiornamento del metodo di pagamento
    if (subscription.requiresPaymentMethod) {
      return {
        isActive: false,
        state: subscription.status,
        reason: 'subscription.errors.paymentMethodRequired',
        canCreateContent: false,
        requiresPaymentMethod: true
      };
    }

    // Verifica se l'abbonamento è scaduto
    if (endDate && endDate < now) {
      return {
        isActive: false,
        state: SUBSCRIPTION_STATES.EXPIRED,
        reason: 'subscription.errors.expired',
        canCreateContent: false,
        requiresPaymentMethod: false
      };
    }

    // Verifica se l'abbonamento è stato annullato
    if (subscription.status === SUBSCRIPTION_STATES.CANCELED) {
      // Se lo stato è "canceled", l'abbonamento è completamente cancellato
      return {
        isActive: false,
        state: SUBSCRIPTION_STATES.CANCELED,
        reason: 'subscription.errors.canceled',
        canCreateContent: false,
        requiresPaymentMethod: false
      };
    }
    
    // Verifica se l'abbonamento è stato annullato ma è ancora attivo
    if (subscription.cancel_at_period_end === true && subscription.status === SUBSCRIPTION_STATES.ACTIVE) {
      // Se cancel_at_period_end è true ma lo stato è ancora "active", l'abbonamento è annullato ma ancora attivo
      return {
        isActive: true,
        state: SUBSCRIPTION_STATES.ACTIVE,
        reason: null,
        canCreateContent: true,
        requiresPaymentMethod: false,
        isCanceled: true, // Flag per indicare che è stato cancellato ma è ancora attivo
        deviceLimitReached: false,
        partLimitReached: false,
        remainingDevices: Infinity,
        remainingParts: Infinity
      };
    }

    // Verifica lo stato dell'abbonamento
    if (subscription.status !== SUBSCRIPTION_STATES.ACTIVE) {
      return {
        isActive: false,
        state: subscription.status,
        reason: 'subscription.errors.inactive',
        canCreateContent: false,
        requiresPaymentMethod: subscription.status === SUBSCRIPTION_STATES.PAST_DUE || 
                              subscription.status === SUBSCRIPTION_STATES.UNPAID
      };
    }

    // Abbonamento premium attivo
    return {
      isActive: true,
      state: SUBSCRIPTION_STATES.ACTIVE,
      reason: null,
      canCreateContent: true,
      requiresPaymentMethod: false,
      deviceLimitReached: false,
      partLimitReached: false,
      remainingDevices: Infinity,
      remainingParts: Infinity
    };
  },

  // Manteniamo il metodo esistente per retrocompatibilità, ma utilizziamo la nuova logica
  checkSubscriptionStatus(subscription) {
    const status = this.getSubscriptionStatus(subscription);
    return status.isActive;
  },

  async update(subscription) {
    try {
      if (!auth.currentUser) {
        throw new Error('Utente non autenticato');
      }

      const userId = auth.currentUser.uid;
      const subscriptionRef = doc(db, `users/${userId}/subscription/default`);
      
      // Crea una copia dell'oggetto subscription
      const updateData = {
        ...subscription,
        updatedAt: serverTimestamp()
      };
      
      // Rimuovi tutti i campi undefined o null
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === undefined || updateData[key] === null) {
          delete updateData[key];
        }
      });

      // Se non ci sono dati da aggiornare, esci
      if (Object.keys(updateData).length === 0) {
        console.log('Nessun dato valido da aggiornare');
        return { data: subscription };
      }

      console.log('Aggiornamento abbonamento con dati:', updateData);
      
      await setDoc(subscriptionRef, updateData, { merge: true });
      return { data: updateData };
    } catch (error) {
      console.error('Errore nell\'aggiornamento dell\'abbonamento:', error);
      throw new Error('Errore nell\'aggiornamento dell\'abbonamento: ' + error.message);
    }
  },

  async incrementDeviceCount() {
    try {
      if (!auth.currentUser) {
        throw new Error('Utente non autenticato');
      }

      const userId = auth.currentUser.uid;
      const subscriptionRef = doc(db, `users/${userId}/subscription/default`);
      const subscriptionSnap = await getDoc(subscriptionRef);
      
      if (subscriptionSnap.exists()) {
        const subscription = subscriptionSnap.data();
        const updatedSubscription = {
          ...subscription,
          devicesCreated: (subscription.devicesCreated || 0) + 1,
          updatedAt: serverTimestamp()
        };
        
        // Salva l'abbonamento aggiornato
        await setDoc(subscriptionRef, updatedSubscription, { merge: true });
        
        // Restituisci l'abbonamento aggiornato con il nuovo contatore
        return { data: updatedSubscription };
      }
      throw new Error('Abbonamento non trovato');
    } catch (error) {
      console.error('Errore nell\'incremento del conteggio dei dispositivi:', error);
      throw error;
    }
  },

  async incrementPartCount() {
    try {
      if (!auth.currentUser) {
        throw new Error('Utente non autenticato');
      }

      const userId = auth.currentUser.uid;
      const subscriptionRef = doc(db, `users/${userId}/subscription/default`);
      const subscriptionSnap = await getDoc(subscriptionRef);
      
      if (subscriptionSnap.exists()) {
        const subscription = subscriptionSnap.data();
        const updatedSubscription = {
          ...subscription,
          partsCreated: (subscription.partsCreated || 0) + 1,
          updatedAt: serverTimestamp()
        };
        
        // Salva l'abbonamento aggiornato
        await setDoc(subscriptionRef, updatedSubscription, { merge: true });
        
        // Restituisci l'abbonamento aggiornato con il nuovo contatore
        return { data: updatedSubscription };
      }
      throw new Error('Abbonamento non trovato');
    } catch (error) {
      console.error('Errore nell\'incremento del conteggio dei ricambi:', error);
      throw error;
    }
  },

  async canCreateDevice() {
    try {
      if (!auth.currentUser) {
        throw new Error('Utente non autenticato');
      }

      // Ottieni l'abbonamento aggiornato in un'unica operazione
      const { data: subscription } = await this.getWithOptionalRefresh();
      const status = this.getSubscriptionStatus(subscription);
      
      if (!status.canCreateContent) {
        return { 
          canCreate: false, 
          reason: status.reason 
        };
      }
      
      if (status.deviceLimitReached) {
          return { 
            canCreate: false, 
            reason: 'subscription.errors.deviceLimitReached'
          };
      }
      
      return { canCreate: true };
    } catch (error) {
      console.error('Errore nella verifica della possibilità di creare dispositivi:', error);
      throw error;
    }
  },

  async canCreatePart() {
    try {
      if (!auth.currentUser) {
        throw new Error('Utente non autenticato');
      }

      // Ottieni l'abbonamento aggiornato in un'unica operazione
      const { data: subscription } = await this.getWithOptionalRefresh();
      const status = this.getSubscriptionStatus(subscription);
      
      if (!status.canCreateContent) {
        return { 
          canCreate: false, 
          reason: status.reason 
        };
      }
      
      if (status.partLimitReached) {
          return { 
            canCreate: false, 
            reason: 'subscription.errors.partLimitReached'
          };
      }
      
      return { canCreate: true };
    } catch (error) {
      console.error('Errore nella verifica della possibilità di creare ricambi:', error);
      throw error;
    }
  },

  // Metodo per aggiornare a un piano a pagamento
  async upgradeToPaid(planDetails) {
    try {
      if (!auth.currentUser) {
        throw new Error('Utente non autenticato');
      }

      const userId = auth.currentUser.uid;
      console.log('Aggiornamento abbonamento per utente:', userId, 'con dettagli:', planDetails);
      
      // Verifica se esiste già un documento di abbonamento
      const subscriptionRef = doc(db, `users/${userId}/subscription/default`);
      const subscriptionDoc = await getDoc(subscriptionRef);
      const currentSubscription = subscriptionDoc.exists() ? subscriptionDoc.data() : null;
      
      // Calcola la data di fine abbonamento (1 anno dal momento attuale)
      const endDate = new Date();
      endDate.setFullYear(endDate.getFullYear() + 1);
      
      // Prepara i dati dell'abbonamento
      const paidSubscription = {
        plan: 'premium',
        status: 'active',
        isActive: true,
        deviceLimit: Infinity,
        partLimit: Infinity,
        startDate: serverTimestamp(),
        endDate: endDate,
        updatedAt: serverTimestamp(),
        stripeCustomerId: planDetails.customerId || null,
        stripeSubscriptionId: planDetails.subscriptionId || null,
        paymentMethod: planDetails.paymentMethod || 'card',
        // Mantieni i contatori esistenti o inizializzali a 0
        devicesCreated: currentSubscription?.devicesCreated || 0,
        partsCreated: currentSubscription?.partsCreated || 0
      };
      
      console.log('Salvataggio abbonamento premium:', paidSubscription);
      await setDoc(subscriptionRef, paidSubscription, { merge: true });
      return { success: true, data: paidSubscription };
    } catch (error) {
      console.error('Errore nell\'aggiornamento dell\'abbonamento:', error);
      throw error;
    }
  },

  // Metodo per verificare lo stato del pagamento
  async checkPaymentStatus(sessionId) {
    try {
      if (!auth.currentUser) {
        throw new Error('Utente non autenticato');
      }
      
      // Questa funzione dovrebbe chiamare una Cloud Function che verifica lo stato della sessione
      // Per semplicità, assumiamo che il pagamento sia andato a buon fine se abbiamo un sessionId
      if (sessionId) {
        return { success: true, status: 'completed' };
      } else {
        return { success: false, status: 'failed' };
      }
    } catch (error) {
      console.error('Errore nella verifica dello stato del pagamento:', error);
      return { success: false, message: error.message };
    }
  },

  // Metodo per rinnovare un abbonamento scaduto
  async renewSubscription() {
    try {
      if (!auth.currentUser) {
        throw new Error('Utente non autenticato');
      }

      const { data: subscription } = await this.get();
      
      if (subscription.plan === 'free') {
        throw new Error('Non è possibile rinnovare un piano gratuito');
      }
      
      // Calcola la nuova data di fine abbonamento (1 anno dalla data di fine attuale)
      let newEndDate;
      if (subscription.endDate) {
        const currentEndDate = subscription.endDate.toDate ? 
          subscription.endDate.toDate() : 
          new Date(subscription.endDate);
        newEndDate = new Date(currentEndDate);
        newEndDate.setFullYear(newEndDate.getFullYear() + 1);
      } else {
        newEndDate = new Date();
        newEndDate.setFullYear(newEndDate.getFullYear() + 1);
      }
      
      const updatedSubscription = {
        ...subscription,
        status: 'active',
        endDate: newEndDate,
        updatedAt: serverTimestamp()
      };
      
      await this.update(updatedSubscription);
      return { data: updatedSubscription };
    } catch (error) {
      console.error('Errore nel rinnovo dell\'abbonamento:', error);
      throw error;
    }
  },

  // Metodo per ascoltare i cambiamenti dell'abbonamento Stripe
  subscribeToSubscriptionChanges(callback) {
    if (!auth.currentUser) {
      console.error('Utente non autenticato');
      return () => {};
    }

    const userId = auth.currentUser.uid;
    const stripeSubscriptionsRef = collection(db, `users/${userId}/subscriptions`);
    
    // Monitora tutti gli abbonamenti Stripe
    const q = query(stripeSubscriptionsRef);
    
    // Utilizziamo un flag per evitare aggiornamenti multipli ravvicinati
    let isProcessing = false;
    
    return onSnapshot(q, async (snapshot) => {
      // Evita elaborazioni multiple simultanee
      if (isProcessing) {
        return;
      }
      
      isProcessing = true;
      
      try {
        // Verifica se ci sono abbonamenti
        const subscriptions = snapshot.docs.map(doc => doc.data());
        
        if (subscriptions.length > 0) {
          // Ottieni l'abbonamento corrente
          const subscriptionRef = doc(db, `users/${userId}/subscription/default`);
          const currentSubscriptionSnap = await getDoc(subscriptionRef);
          const currentSubscription = currentSubscriptionSnap.exists() ? currentSubscriptionSnap.data() : { devicesCreated: 0, partsCreated: 0 };
          
          // Ordina gli abbonamenti per data di creazione (più recente prima)
          const sortedSubscriptions = this.sortSubscriptionsByRecency(subscriptions);
          
          // Usa il primo abbonamento dopo l'ordinamento (il più recente)
          const selectedSubscription = sortedSubscriptions[0];
          
          if (selectedSubscription) {
            // Determina se l'abbonamento è attivo in base allo stato
            const isActive = selectedSubscription.status === 'active';
            
            // Aggiorna il documento principale con l'abbonamento selezionato
            const updatedSubscription = {
              plan: 'premium',
              status: selectedSubscription.status,
              isActive: isActive,
              deviceLimit: Infinity,
              partLimit: Infinity,
              startDate: selectedSubscription.current_period_start || serverTimestamp(),
              endDate: selectedSubscription.current_period_end || null,
              cancelAtPeriodEnd: selectedSubscription.cancel_at_period_end || false,
              canceledAt: selectedSubscription.canceled_at || null,
              stripeCustomerId: selectedSubscription.customer || null,
              stripeSubscriptionId: selectedSubscription.id || null,
              requiresPaymentMethod: selectedSubscription.status === 'past_due' || selectedSubscription.status === 'unpaid',
              updatedAt: serverTimestamp(),
              // Mantieni i contatori esistenti
              devicesCreated: currentSubscription?.devicesCreated || 0,
              partsCreated: currentSubscription?.partsCreated || 0
            };
            
            // Aggiorna la cache degli abbonamenti Stripe
            stripeSubscriptionsCache = {
              data: subscriptions,
              timestamp: Date.now()
            };
            
            await setDoc(subscriptionRef, updatedSubscription, { merge: true });
            
            // Notifica il callback con i dati aggiornati
            callback({
              data: updatedSubscription
            });
          }
        }
      } catch (error) {
        console.error('Errore nell\'elaborazione del cambiamento dell\'abbonamento:', error);
      } finally {
        isProcessing = false;
      }
    });
  },

  // Metodo per verificare se un abbonamento è scaduto e aggiornarlo
  async checkAndUpdateExpiredSubscription(subscription) {
    if (!subscription || !auth.currentUser) {
      return { updated: false, reason: 'Abbonamento o utente non disponibile' };
    }

    const status = this.getSubscriptionStatus(subscription);
    
    // Se l'abbonamento è scaduto o cancellato ma lo stato non è aggiornato
    if (!status.isActive && subscription.status === SUBSCRIPTION_STATES.ACTIVE) {
      try {
        // Crea un nuovo oggetto con solo i campi che vogliamo aggiornare
        const updateData = {
          status: status.state,
          isActive: false,
          // Mantieni gli altri campi importanti
          plan: subscription.plan,
          deviceLimit: subscription.deviceLimit,
          partLimit: subscription.partLimit,
          devicesCreated: subscription.devicesCreated,
          partsCreated: subscription.partsCreated,
          startDate: subscription.startDate,
          endDate: subscription.endDate,
          stripeCustomerId: subscription.stripeCustomerId || null,
          stripeSubscriptionId: subscription.stripeSubscriptionId || null
        };
        
        console.log('Aggiornamento abbonamento scaduto con dati:', updateData);
        
        const result = await this.update(updateData);
        return { updated: true, data: result.data };
      } catch (err) {
        console.error('Errore nell\'aggiornamento dello stato dell\'abbonamento:', err);
        return { updated: false, reason: err.message };
      }
    }
    
    return { updated: false, reason: 'Nessun aggiornamento necessario' };
  },

  async getLatestSubscriptionStatus() {
    try {
      if (!auth.currentUser) {
        throw new Error('Utente non autenticato');
      }
      
      const userId = auth.currentUser.uid;
      
      // Leggi le sottoscrizioni dall'estensione Stripe
      const subscriptionsRef = collection(db, `users/${userId}/subscriptions`);
      const subscriptionsSnap = await getDocs(
        query(subscriptionsRef, where('status', 'in', ['active', 'past_due', 'unpaid', 'canceled']))
      );
      
      if (!subscriptionsSnap.empty) {
        // Ottieni tutti gli abbonamenti
        const subscriptions = subscriptionsSnap.docs.map(doc => doc.data());
        
        // Ordina gli abbonamenti solo in base alla data di creazione, prendendo il più recente
        const sortedSubscriptions = [...subscriptions].sort((a, b) => {
          // Ordina per data di creazione (se disponibile)
          if (a.created && b.created) {
            return b.created - a.created; // Il più recente prima
          }
          
          // Se non c'è data di creazione, usa la data di inizio periodo
          if (a.current_period_start && b.current_period_start) {
            return b.current_period_start - a.current_period_start;
          }
          
          return 0;
        });
        
        // Usa il primo abbonamento dopo l'ordinamento (il più recente)
        const stripeSubscription = sortedSubscriptions[0];
        
        // Aggiorna il documento dell'abbonamento principale
        const subscriptionRef = doc(db, `users/${userId}/subscription/default`);
        const updatedSubscription = {
          plan: 'premium',
          status: stripeSubscription.status,
          isActive: stripeSubscription.status === 'active',
          stripeCustomerId: stripeSubscription.customer,
          stripeSubscriptionId: stripeSubscription.id,
          startDate: new Date(stripeSubscription.current_period_start * 1000),
          endDate: new Date(stripeSubscription.current_period_end * 1000),
          cancelAtPeriodEnd: stripeSubscription.cancel_at_period_end,
          updatedAt: serverTimestamp()
        };
        
        await setDoc(subscriptionRef, updatedSubscription, { merge: true });
        return { data: updatedSubscription };
      }
      
      // Se non ci sono abbonamenti Stripe, restituisci l'abbonamento esistente
      const currentSubscriptionRef = doc(db, `users/${userId}/subscription/default`);
      const currentSubscriptionSnap = await getDoc(currentSubscriptionRef);
      if (currentSubscriptionSnap.exists()) {
        return { data: currentSubscriptionSnap.data() };
      }
      
      // Altrimenti restituisci un abbonamento gratuito predefinito
      return { data: {
        plan: 'free',
        status: 'active',
        isActive: true,
        deviceLimit: 20,
        partLimit: 20
      }};
    } catch (error) {
      console.error('Errore nel recupero dello stato dell\'abbonamento:', error);
      throw error;
    }
  },

  async createCustomerPortalSession() {
    try {
      if (!auth.currentUser) {
        throw new Error('Utente non autenticato');
      }

      const userId = auth.currentUser.uid;
      
      // Usa la funzione dell'estensione ufficiale
      const portalSessionRef = doc(db, `users/${userId}/checkout_sessions`, 'portal');
      await setDoc(portalSessionRef, {
        returnUrl: `${window.location.origin}/settings/subscription`,
        locale: 'it'
      });
      
      // Attendi che l'estensione generi l'URL del portale
      const unsubscribe = onSnapshot(portalSessionRef, (snapshot) => {
        const data = snapshot.data();
        if (data?.url) {
          window.location.assign(data.url);
          unsubscribe();
        }
      });
      
      return { success: true };
    } catch (error) {
      console.error('Errore nella creazione del portale cliente:', error);
      throw error;
    }
  },

  /**
   * Forza l'aggiornamento dello stato dell'abbonamento controllando tutti gli abbonamenti Stripe
   * e aggiornando il documento principale con l'abbonamento attivo, se presente.
   */
  async forceRefreshSubscription() {
    try {
      if (!auth.currentUser) {
        throw new Error('Utente non autenticato');
      }

      const userId = auth.currentUser.uid;
      
      // Riferimenti ai documenti
      const subscriptionRef = doc(db, `users/${userId}/subscription/default`);
      
      // Ottieni l'abbonamento corrente
      const subscriptionSnap = await getDoc(subscriptionRef);
      const currentSubscription = subscriptionSnap.exists() ? subscriptionSnap.data() : null;
      
      // Recupera gli abbonamenti Stripe dalla cache o da Firestore
      const subscriptions = await this.getStripeSubscriptionsWithCache(userId);
      
      if (subscriptions && subscriptions.length > 0) {
        // Ordina gli abbonamenti per data di creazione (più recente prima)
        const sortedSubscriptions = this.sortSubscriptionsByRecency(subscriptions);
        
        // Usa il primo abbonamento dopo l'ordinamento (il più recente)
        const selectedSubscription = sortedSubscriptions[0];
        
        if (selectedSubscription) {
          // Determina se l'abbonamento è attivo in base allo stato
          const isActive = selectedSubscription.status === 'active';
          
          // Verifica se l'abbonamento corrente è già aggiornato
          if (currentSubscription && 
              currentSubscription.stripeSubscriptionId === selectedSubscription.id &&
              currentSubscription.status === selectedSubscription.status &&
              currentSubscription.isActive === isActive) {
            
            // Aggiorna solo il timestamp se necessario
            if (!currentSubscription.updatedAt || 
                (Date.now() - currentSubscription.updatedAt.toMillis()) > CACHE_VALIDITY_TIME) {
              await updateDoc(subscriptionRef, { updatedAt: serverTimestamp() });
              currentSubscription.updatedAt = serverTimestamp();
            }
            
            return { success: true, data: currentSubscription, updated: false };
          }
          
          // Aggiorna il documento principale con l'abbonamento selezionato
          const updatedSubscription = {
            plan: 'premium',
            status: selectedSubscription.status,
            isActive: isActive,
            deviceLimit: Infinity,
            partLimit: Infinity,
            startDate: selectedSubscription.current_period_start || serverTimestamp(),
            endDate: selectedSubscription.current_period_end || null,
            cancelAtPeriodEnd: selectedSubscription.cancel_at_period_end || false,
            canceledAt: selectedSubscription.canceled_at || null,
            stripeCustomerId: selectedSubscription.customer || null,
            stripeSubscriptionId: selectedSubscription.id || null,
            requiresPaymentMethod: selectedSubscription.status === 'past_due' || selectedSubscription.status === 'unpaid',
            updatedAt: serverTimestamp(),
            // Mantieni i contatori esistenti
            devicesCreated: currentSubscription?.devicesCreated || 0,
            partsCreated: currentSubscription?.partsCreated || 0
          };
          
            await setDoc(subscriptionRef, updatedSubscription, { merge: true });
            return { success: true, data: updatedSubscription, updated: true };
        }
      }
      
      // Se non ci sono abbonamenti, restituisci l'abbonamento corrente
      if (currentSubscription) {
        // Aggiorna il timestamp per evitare verifiche frequenti
        if (!currentSubscription.updatedAt) {
          await updateDoc(subscriptionRef, { updatedAt: serverTimestamp() });
        }
      }
      
      return { 
        success: false, 
        message: 'Nessun abbonamento trovato', 
        data: currentSubscription,
        updated: false
      };
    } catch (error) {
      console.error('Errore nell\'aggiornamento forzato dell\'abbonamento:', error);
      throw error;
    }
  }
};

export { SUBSCRIPTION_STATES };
export default subscriptionService; 